import { DirectUploadConfig, FileRules, UploadError } from '@execonline-inc/file-upload.private';
import { identity } from '@kofno/piper';
import { Header } from 'ajaxian';
import { Task } from 'taskarian';
import { AppyError, clientHeadersT } from '../Appy';
import { CurrentUserResource } from '../CurrentUser/Types';
import { MissingLinkError, findLinkT } from '../LinkyLinky';
import { Link } from '../Resource/Types';
import { TPlainTextKey } from '../Translations';

/**
 * Everything that can go wrong between the user picking a file and the blob
 * being ready to submit. Fetching the config can fail before an upload even
 * starts, so both halves share one error type and the components handle one
 * exhaustive switch.
 */
export type DirectUploadFailure = AppyError | MissingLinkError | UploadError;

/**
 * Rules for each kind of upload. They mirror the matching entry in
 * DirectUpload::Purpose on the server, which is the gate. These run first so
 * the user learns a file is wrong before it is read and hashed.
 *
 * Profile photos take images only, because the avatar variant is generated
 * by ImageMagick and cannot render a document.
 */
export const profilePhotoRules: FileRules = {
  extensions: ['jpg', 'jpeg', 'png', 'gif'],
  maxBytes: 10 * 1024 * 1024,
  maxFiles: 1,
};

export const attachmentRules = (extensions: ReadonlyArray<string>): FileRules => ({
  extensions,
  maxBytes: 250 * 1024 * 1024,
  maxFiles: 5,
});

const toHeaderRecord = (headers: ReadonlyArray<Header>): Record<string, string> =>
  headers.reduce((soFar, h) => ({ ...soFar, [h.field]: h.value }), {});

/**
 * Builds the upload config from the current user's `direct-upload` link and
 * the same auth headers every other API call sends. The endpoint runs inside
 * ApplicationController, so it needs them.
 */
export const directUploadConfig = (
  currentUserResource: CurrentUserResource,
  purpose: string,
  onProgress?: (fraction: number) => void,
): Task<DirectUploadFailure, DirectUploadConfig> =>
  Task.succeed<DirectUploadFailure, ReadonlyArray<Link>>(currentUserResource.links)
    .andThen(findLinkT('direct-upload'))
    .assign('headers', clientHeadersT().mapError<DirectUploadFailure>(identity))
    .map(({ href, headers }) => ({
      endpoint: href,
      purpose,
      withCredentials: true,
      headers: toHeaderRecord(headers),
      onProgress,
    }));

export const photoErrorMessage = (error: DirectUploadFailure): TPlainTextKey => {
  switch (error.kind) {
    case 'unsupported-file-type':
      return 'Upload a photo in PNG, JPEG or GIF format';
    case 'file-too-large':
      return 'That file is too large to upload';
    case 'empty-file':
      return 'That file is empty';
    case 'missing-link-error':
    case 'missing-application-id':
    case 'missing-api-compatibility':
      return 'Profile picture uploading is not available';
    default:
      return 'Your upload could not be processed at this time';
  }
};

export const attachmentErrorMessage = (error: DirectUploadFailure): TPlainTextKey => {
  switch (error.kind) {
    case 'unsupported-file-type':
      return 'This file type is not supported. Please upload a PDF, Word, PowerPoint, Excel, PNG, or JPEG file.';
    case 'file-too-large':
      return 'That file is too large to upload';
    case 'empty-file':
      return 'That file is empty';
    case 'too-many-files':
      return 'You can only upload 5 files at a time';
    default:
      return 'Your upload could not be processed at this time';
  }
};
